import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import ContentfulCardCarousel from './ContentfulCardCarousel'
import ContentfulFlightDealsCarousel from './ContentfulFlightDealsCarousel'
import BodyText from 'LuxKit/Typography/BodyText'
import ContentfulOfferCarousel from './ContentfulOfferCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import ContentfulExperienceOfferCarousel from './ContentfulExperienceOfferCarousel'
import SupportTile from 'components/Common/SupportTile/SupportTile'
import SearchHero from './SearchHero'
import ContentfulHeroOfferCarouselSection from './ContentfulHeroOfferCarouselSection'
import AdditionalGuides from 'components/Pages/LandingPage/Hub/AdditionalGuides/AdditionalGuides'
import BannerCarousel from 'components/Common/Banner/BannerCarousel'
import { ContentfulVideoBlock } from './ContentfulVideoBlock'
import Reviews from 'components/Pages/AboutUsPage/Reviews/Reviews'
import ContentfulImageTileCarousel from 'marketing/pages/ContentfulWebPage/ContentfulImageTileCarousel'
import ContentfulValuePropositionBanner from 'marketing/pages/ContentfulWebPage/ContentfulValuePropositionBanner'
import { renderMarkdown } from 'lib/string/markdownUtils'
import ContentfulVerticalOfferList from 'marketing/pages/ContentfulWebPage/ContentfulVerticalOfferList'
import FrequentlyAskedQuestions from 'components/Content/FrequentlyAskedQuestions'
import ContentfulIframeWrapper from './ContentfulIframe'
import ContentfulLogosCarousel from './ContentfulLogosCarousel'
import ContentfulForm from './ContentfulForm'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { toPascalCase } from 'lib/string/stringUtils'
import AdsLandingPage from 'components/Pages/AdsLandingPage/AdsLandingPage'
import ContentfulSpacer from './ContentfulSpacer'
import ContentfulCampaignHero from './ContentfulCampaignHero'
import ContentfulBannerGroup from './ContentfulBannerGroup'
import ContentfulOfferGrid from './ContentfulOfferGrid'
import ContentfulCruiseOfferList from './ContentfulCruiseOfferList'

const BannerCarouselWrapper = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

type Props = App.ContentfulWebPageComponent

const LIVE_OFFERS_PATH = '/campaign/live-offers'

function ContentfulWebPageComponent(props: Props) {
  const intl = useIntl()
  const location = useLocation()
  const title = renderMarkdown(props.title)
  const hideLookingForSomethingElse = location.pathname.endsWith(LIVE_OFFERS_PATH)
  const tracking = useOfferListTracking(
    getListName('OfferList', 'ContentfulWebPage', toPascalCase(props.title ?? '')),
  )

  const unknownComponentText = intl.formatMessage({
    id: 'afQFpAoZH3IIqPiPoTdM',
    defaultMessage: 'Unknown component',
    description: 'Error message shown when an unknown Contentful component type is encountered',
  })

  function renderComponent() {
    switch (props.contentTypeId) {
      case 'componentHeroImage':
        return <SearchHero {...props} />
      case 'componentHeroCarousel':
        return <ContentfulHeroOfferCarouselSection title={title} filters={props.filters} overrideOfferIds={props.offerIds} />
      case 'componentCardCarousel':
        return <ContentfulCardCarousel
          title={title}
          subtitle={props.subtitle}
          cardItems={props.cardItems}
          viewAllLink={props.viewAllLink}
          isLargeContentWrapper={props.isLargeContentWrapper}
        />
      case 'componentExperienceOfferListCarousel':
        return <div>
          <ContentfulExperienceOfferCarousel title={title} destination={props.destination} campaigns={props.campaigns} hideLookingForSomethingElse={hideLookingForSomethingElse} />
        </div>
      case 'componentOfferListCarousel':
        return <div><ContentfulOfferCarousel {...props} title={title} hideLookingForSomethingElse={hideLookingForSomethingElse} />
        </div>
      case 'componentSupport':
        return <LayoutContainer>
          <SupportTile />
        </LayoutContainer>
      case 'componentValuePropositionBanner':
        return <LayoutContainer>
          <ContentfulValuePropositionBanner {...props} />
        </LayoutContainer>
      case 'componentTileNavigation':
        return <AdditionalGuides
          {...props}
        />
      case 'componentWings':
        return <BannerCarouselWrapper>
          <BannerCarousel banners={props.banners} />
        </BannerCarouselWrapper>
      case 'componentVideoBlock':
        return <ContentfulVideoBlock {...props} title={title} />
      case 'componentReviews':
        return <Reviews {...props} />
      case 'componentImageTileCarousel':
        return <ContentfulImageTileCarousel {...props} />
      case 'componentVerticalOfferList':
        return <ContentfulVerticalOfferList {...props} />
      case 'componentFaqs':
        return <LayoutContainer>
          <FrequentlyAskedQuestions
            heading={props.title}
            items={props.content}
            maxToShow={10}
          />
        </LayoutContainer>
      case 'componentIframe':
        return <ContentfulIframeWrapper {...props} />
      case 'componentLogoCarousel':
        return <ContentfulLogosCarousel {...props} title={title} />
      case 'componentFlightDealsCarousal':
        return <ContentfulFlightDealsCarousel {...props} title={title} />
      case 'componentForm':
        return <ContentfulForm {...props} />
      case 'componentVerticalOfferListsGroup':
        return <AdsLandingPage offerListsGroup={props.offerListsGroup} />
      case 'componentSpacer':
        return <ContentfulSpacer {...props} />
      case 'componentCampaignHero':
        return <ContentfulCampaignHero {...props} title={title} />
      case 'componentBannerGroup':
        return <ContentfulBannerGroup {...props} />
      case 'componentOfferGridView':
        return <ContentfulOfferGrid
          filters={props.offerIds?.length ? undefined : { offerTypes: props.offerTypes, limit: props.offersToShow, placeIds: props.placeIds, campaigns: props.campaigns }}
          overrideOfferList={props.offerIds?.length ? { offerIds: props.offerIds, fetching: false, error: null, key: 'contentful-override' } : undefined}
        />
      case 'componentCruiseOfferList':
        return <ContentfulCruiseOfferList {...props} title={title} />
      default:
        return <BodyText variant="medium">{unknownComponentText}</BodyText>
    }
  }

  return <OfferListEventsProvider tracking={tracking}>{renderComponent()}</OfferListEventsProvider>
}

export default ContentfulWebPageComponent
